// Every request below goes through apiFetch, the one place the global fetch
// is called for the API and X-Interface-Language is attached (flag on, non-
// English only).
import { apiFetch } from '../i18n/apiFetch'
import { apiUrl } from './apiUrl'

// apiUrl lives in ./apiUrl (so apiFetch can read it without a cycle) and is
// re-exported here for every existing importer.
export { apiUrl }

export class ApiError extends Error {
  status: number
  statusText: string
  body?: string
  /** The response's Retry-After header (seconds or an HTTP date), when sent. */
  retryAfter?: string

  constructor(status: number, statusText: string, body?: string, retryAfter?: string | null) {
    // eslint-disable-next-line i18next/no-literal-string -- HTTP header, or the English developer message (screens render the API detail or catalog copy, never this)
    super(`API Error ${status}: ${statusText}`)
    this.status = status
    this.statusText = statusText
    this.body = body
    if (retryAfter) this.retryAfter = retryAfter
  }

  /**
   * The API's own explanation, from FastAPI's `{"detail": "..."}` body.
   *
   * `message` is deliberately left alone: it reads `API Error ${status}:
   * ${statusText}`, and over HTTP/2 `statusText` is always "", so every error
   * surface that prints it shows a blank reason. Several components map errors
   * to their own copy and assert the raw ApiError string is never rendered, so
   * this is an additive accessor rather than a change to `message`.
   *
   * Returns undefined for a non-JSON body, a missing key, a non-string detail
   * (FastAPI 422s send an array), or a blank/whitespace-only string - each of
   * which would otherwise reproduce the blank reason this exists to fix.
   */
  get detail(): string | undefined {
    if (!this.body) return undefined
    let parsed: unknown
    try {
      parsed = JSON.parse(this.body)
    } catch {
      return undefined
    }
    if (typeof parsed !== 'object' || parsed === null) return undefined
    const value = (parsed as { detail?: unknown }).detail
    if (typeof value !== 'string') return undefined
    const trimmed = value.trim()
    return trimmed === '' ? undefined : trimmed
  }

  /**
   * The API's machine error code, from a top-level `"code"` beside `detail`,
   * parsed exactly like `detail` (same undefined cases). Today the only one
   * is `source_private`, which the API adds to the "access denied. The deck
   * may be private." detail ONLY on a localized (non-English) response
   * (mtg-api i18n 3-api.2). An English response never carries it, so callers
   * keep their English-text fallback for it.
   */
  get code(): string | undefined {
    if (!this.body) return undefined
    let parsed: unknown
    try {
      parsed = JSON.parse(this.body)
    } catch {
      return undefined
    }
    if (typeof parsed !== 'object' || parsed === null) return undefined
    const value = (parsed as { code?: unknown }).code
    if (typeof value !== 'string') return undefined
    const trimmed = value.trim()
    return trimmed === '' ? undefined : trimmed
  }
}

export async function apiPost<T>(
  path: string,
  body: object,
  options?: { bearerToken?: string; signal?: AbortSignal },
): Promise<T> {
  // eslint-disable-next-line i18next/no-literal-string -- HTTP header, or the English developer message (screens render the API detail or catalog copy, never this)
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (options?.bearerToken) {
    // eslint-disable-next-line i18next/no-literal-string -- HTTP header, or the English developer message (screens render the API detail or catalog copy, never this)
    headers['Authorization'] = `Bearer ${options.bearerToken}`
  }
  // A caller's signal (optional) aborts the request too; the timeout stays.
  const timeout = AbortSignal.timeout(300_000)
  const signal = options?.signal && typeof AbortSignal.any === 'function'
    ? AbortSignal.any([options.signal, timeout])
    : timeout
  const res = await apiFetch(apiUrl(path), {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
    signal,
  })

  if (!res.ok) {
    const text = await res.text().catch(() => '')
    // eslint-disable-next-line i18next/no-literal-string -- an HTTP header name
    throw new ApiError(res.status, res.statusText, text, res.status === 429 ? res.headers?.get?.('Retry-After') : null)
  }

  return res.json() as Promise<T>
}

export async function apiGet<T>(
  path: string,
  // `cache` is passed straight to fetch(). Needed by the owned-name version
  // probe: a probe answered from the HTTP cache returns the OLD version and
  // recreates the stale-cache defect it exists to detect. Omitted elsewhere,
  // so every existing caller keeps default caching.
  options?: { bearerToken?: string; cache?: RequestCache },
): Promise<T> {
  const headers: Record<string, string> = {}
  if (options?.bearerToken) {
    // eslint-disable-next-line i18next/no-literal-string -- HTTP header, or the English developer message (screens render the API detail or catalog copy, never this)
    headers['Authorization'] = `Bearer ${options.bearerToken}`
  }
  const res = await apiFetch(apiUrl(path), {
    headers,
    ...(options?.cache ? { cache: options.cache } : {}),
    signal: AbortSignal.timeout(30_000),
  })

  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new ApiError(res.status, res.statusText, text)
  }

  return res.json() as Promise<T>
}

export async function apiPatch<T>(
  path: string,
  body: object,
  options?: { bearerToken?: string },
): Promise<T> {
  // eslint-disable-next-line i18next/no-literal-string -- HTTP header, or the English developer message (screens render the API detail or catalog copy, never this)
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (options?.bearerToken) {
    // eslint-disable-next-line i18next/no-literal-string -- HTTP header, or the English developer message (screens render the API detail or catalog copy, never this)
    headers['Authorization'] = `Bearer ${options.bearerToken}`
  }
  const res = await apiFetch(apiUrl(path), {
    method: 'PATCH',
    headers,
    body: JSON.stringify(body),
    signal: AbortSignal.timeout(300_000),
  })

  if (!res.ok) {
    const text = await res.text().catch(() => '')
    // eslint-disable-next-line i18next/no-literal-string -- an HTTP header name
    throw new ApiError(res.status, res.statusText, text, res.status === 429 ? res.headers?.get?.('Retry-After') : null)
  }

  return res.json() as Promise<T>
}

export async function apiDelete<T>(
  path: string,
  options?: { bearerToken?: string },
): Promise<T> {
  // eslint-disable-next-line i18next/no-literal-string -- HTTP header, or the English developer message (screens render the API detail or catalog copy, never this)
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (options?.bearerToken) {
    // eslint-disable-next-line i18next/no-literal-string -- HTTP header, or the English developer message (screens render the API detail or catalog copy, never this)
    headers['Authorization'] = `Bearer ${options.bearerToken}`
  }
  const res = await apiFetch(apiUrl(path), {
    method: 'DELETE',
    headers,
    signal: AbortSignal.timeout(30_000),
  })

  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new ApiError(res.status, res.statusText, text)
  }

  // 204 No Content (DELETE /collections/{id}/copies/{copy_id}) has no body to
  // parse; res.json() would throw on it.
  if (res.status === 204) return undefined as T

  return res.json() as Promise<T>
}

export interface FetchUrlResponse {
  source_url: string
  source_type: string
  decklist: string
  card_count: number
  // Additive as of 2026-08-04. Older API builds omit them, so both are optional
  // and every caller must tolerate undefined.
  name?: string | null
  has_commander?: boolean
}

export async function fetchDeckFromUrl(url: string): Promise<FetchUrlResponse> {
  return apiPost<FetchUrlResponse>('/decks/fetch-url', { url })
}
